import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { CennikZoraditPole } from "@/api/queryKeys";
import type { Zoradenie } from "@/components/cennik/cennikZalozky";

type Props = {
  pole: CennikZoraditPole;
  label: string;
  zoradenie: Zoradenie | null;
  onZmena: (pole: CennikZoraditPole) => void;
  className?: string;
};

const DALSI_KROK = {
  none: "vzostupne",
  ascending: "zostupne",
  descending: "zrušiť triedenie",
} as const;

/**
 * Hlavička stĺpca Cenníka, ktorá triedi (KOLO 166).
 *
 * Triedi server (celý cenník, nie len zobrazená strana) — klik len
 * prepne `zoradit`/`smer`: vzostupne → zostupne → bez triedenia.
 */
export function ZoraditHlavicka({
  pole,
  label,
  zoradenie,
  onZmena,
  className,
}: Props): JSX.Element {
  return (
    <TableHead className={className} aria-sort={ariaSortPola(zoradenie, pole)}>
      <ZoraditTlacidlo pole={pole} label={label} zoradenie={zoradenie} onZmena={onZmena} />
    </TableHead>
  );
}

/** `aria-sort` hlavičky podľa toho, či sa triedi podľa niektorého z polí. */
export function ariaSortPola(
  zoradenie: Zoradenie | null,
  ...polia: CennikZoraditPole[]
): "ascending" | "descending" | "none" {
  if (!zoradenie || !polia.includes(zoradenie.pole)) return "none";
  return zoradenie.smer === "asc" ? "ascending" : "descending";
}

/**
 * Samotné tlačidlo triedenia — hlavička stĺpca Položka (KOLO 197) ich má
 * viac vedľa seba (Názov, Kód, Dekor…), lebo spája niekdajšie stĺpce.
 */
export function ZoraditTlacidlo({
  pole,
  label,
  zoradenie,
  onZmena,
}: Omit<Props, "className">): JSX.Element {
  const ariaSort = ariaSortPola(zoradenie, pole);
  const smer = ariaSort === "ascending" ? "asc" : ariaSort === "descending" ? "desc" : null;
  const Ikona =
    smer === "asc" ? ArrowUp : smer === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onZmena(pole)}
      className={cn(
        "inline-flex items-center gap-1 hover:text-foreground",
        smer !== null && "text-foreground",
      )}
      title={`Zoradiť podľa stĺpca ${label} — ${DALSI_KROK[ariaSort]}`}
    >
      {label}
      <Ikona
        className={cn("h-3 w-3 shrink-0", smer === null && "opacity-40")}
        aria-hidden
      />
    </button>
  );
}
